import { OverlayScrollArea } from '@odo/shared-ui';
import React, { ReactNode } from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import ResourceBreadcrumb, { type ResourceCrumb } from './ResourceBreadcrumb';
import SearchTrigger from './SearchTrigger';

interface DetailRail {
    title: string;
    /** e.g. "3 of 12". */
    positionLabel?: string;
    children: ReactNode;
}

interface ResourceDetailLayoutProps {
    crumbs: ResourceCrumb[];
    searchPlaceholder: string;
    /** Right-hand card of sibling rows; omitted, only the search field shows. */
    rail?: DetailRail | null;
    children: ReactNode;
}

/** Detail-page shell: breadcrumb strip, main card left, search over a full-height rail card right. */
export const ResourceDetailLayout: React.FC<ResourceDetailLayoutProps> = ({
    crumbs,
    searchPlaceholder,
    rail,
    children
}) => {
    const { reserveTopRight } = useResourcesConfig();

    return (
        <div className="flex flex-col min-h-0 flex-1">
            {/* pr-16 keeps clear of the host's overlaid avatar. */}
            <div className={`flex-shrink-0 px-8 pt-8 ${reserveTopRight ? 'pr-16' : ''}`}>
                <ResourceBreadcrumb crumbs={crumbs} />
            </div>

            <div className="flex-1 min-h-0 px-8 pt-5 pb-8">
                <div className="flex gap-4 h-full min-h-0 items-stretch">
                    <div className="flex-1 min-w-0 h-full min-h-0">{children}</div>

                    <div className="hidden xl:flex w-[320px] flex-shrink-0 flex-col gap-4 h-full min-h-0">
                        <SearchTrigger variant="full" placeholder={searchPlaceholder} />

                        {rail && (
                            <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col">
                                <div className="flex items-center gap-2.5 px-5 pt-5 pb-3 flex-shrink-0">
                                    <h2 className="text-sm font-bold text-gray-800 truncate">
                                        {rail.title}
                                    </h2>
                                    {rail.positionLabel && (
                                        <span className="ml-auto text-xs text-gray-500 flex-shrink-0">
                                            {rail.positionLabel}
                                        </span>
                                    )}
                                </div>
                                <OverlayScrollArea
                                    className="flex-1 min-h-0"
                                    contentClassName="px-3 pb-3"
                                    innerClassName="flex flex-col gap-1"
                                    trackInsetPx={8}
                                >
                                    {rail.children}
                                </OverlayScrollArea>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResourceDetailLayout;
